import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Pencil, Plus, Trash2, X, Download, Mail, CheckCircle2, Clock, FileText,
  AlertTriangle, Eye, Wallet, Search, Filter,
} from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { AField, ATextArea, SectionShell, Notice, LoadError } from "./ui";
import { buildInvoiceHtml, printInvoice, fmtDDMMYYYY, fmtGBP } from "./invoicePdf";

// Whole-pound £ format for list tables (e.g. "£12,450"). PDFs/editor keep pence via fmtGBP.
const fmtGBPWhole = (n) =>
  `£${Math.round(Number(n || 0)).toLocaleString("en-GB")}`;

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyLine = () => ({ description: "", quantity: 1, unit_price: 0 });
const emptyInvoice = (settings) => ({
  customer_id: "",
  customer_name: "",
  customer_company: "",
  customer_email: "",
  customer_address: "",
  invoice_date: today(),
  due_date: addDays(today(), 14),
  lines: [emptyLine()],
  vat_enabled: false,
  notes: settings?.default_notes || "",
  payment_terms: settings?.default_payment_terms || "",
  status: "sent",
});

const paidAmount = (inv) => {
  if (inv.paid_amount != null) return Number(inv.paid_amount);
  const payments = inv.payments || [];
  const sum = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  // Legacy invoices marked paid before the payments feature existed.
  if (inv.status === "paid" && payments.length === 0) return Number(inv.total || 0);
  return sum;
};

const balanceDue = (inv) => {
  if (inv.balance_due != null) return Number(inv.balance_due);
  return Math.max(0, Number(inv.total || 0) - paidAmount(inv));
};

const derivedStatus = (inv) => {
  if (inv.status === "draft") return "draft";
  const paid = paidAmount(inv);
  const total = Number(inv.total || 0);
  if (inv.status === "paid" || (total > 0 && paid >= total)) return "paid";
  if (paid > 0) return "part_paid";
  if (inv.due_date && new Date(inv.due_date) < new Date(today())) return "overdue";
  return "unpaid";
};

const StatusChip = ({ inv }) => {
  const s = derivedStatus(inv);
  const map = {
    draft: { label: "Draft", cls: "bg-cream-3 text-forest/70 border-forest/15", Icon: FileText },
    unpaid: { label: "Unpaid", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
    part_paid: { label: "Part Paid", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
    paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    overdue: { label: "Overdue", cls: "bg-red-50 text-red-700 border-red-200", Icon: AlertTriangle },
  };
  const { label, cls, Icon } = map[s] || map.unpaid;
  return (
    <span
      data-testid={`invoice-status-${s}`}
      className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 border ${cls}`}
    >
      <Icon size={11} strokeWidth={1.8} /> {label}
    </span>
  );
};

const PAYMENT_METHODS = [
  "Bank transfer",
  "Card",
  "Cash",
  "Cheque",
  "Direct debit",
  "Other",
];

const PaymentsPanel = ({ invoice, onAdd, onRemove }) => {
  const paid = paidAmount(invoice);
  const total = Number(invoice.total || 0);
  const balance = Math.max(0, total - paid);
  const [amount, setAmount] = useState(balance > 0 ? balance.toFixed(2) : "");
  const [date, setDate] = useState(today());
  const [method, setMethod] = useState("Bank transfer");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const amt = Number(amount);
    if (!(amt > 0)) return;
    setSubmitting(true);
    try {
      await onAdd({ amount: amt, date, method, note });
      setAmount("");
      setNote("");
    } finally {
      setSubmitting(false);
    }
  };

  const payments = (invoice.payments || []).slice().sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  return (
    <section
      data-testid="invoice-payments-panel"
      className="bg-cream-3 border border-forest/10 p-5 sm:p-6 space-y-5"
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h3 className="font-serif text-lg text-forest flex items-center gap-2">
          <Wallet size={18} strokeWidth={1.6} /> Payments
        </h3>
        <div className="flex gap-6 text-right">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-forest/55">Total</p>
            <p className="font-serif text-lg text-forest">{fmtGBP(total)}</p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-forest/55">Paid</p>
            <p className="font-serif text-lg text-forest" data-testid="invoice-paid-amount">{fmtGBP(paid)}</p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-forest/55">Balance</p>
            <p
              className={`font-serif text-lg ${balance > 0 ? "text-gold-dark" : "text-forest/50"}`}
              data-testid="invoice-balance-due"
            >
              {fmtGBP(balance)}
            </p>
          </div>
        </div>
      </div>

      {payments.length > 0 && (
        <div className="bg-white border border-forest/10">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-forest/10 text-[10px] tracking-[0.18em] uppercase text-forest/60">
                <th className="text-left px-4 py-2.5 font-bold">Date</th>
                <th className="text-left px-4 py-2.5 font-bold">Method</th>
                <th className="text-left px-4 py-2.5 font-bold">Note</th>
                <th className="text-right px-4 py-2.5 font-bold">Amount</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {payments.map((p, i) => (
                <tr
                  key={p.id}
                  data-testid={`invoice-payment-row-${i + 1}`}
                  className="border-b border-forest/5 last:border-b-0"
                >
                  <td className="px-4 py-2.5 text-sage">{fmtDDMMYYYY(p.date)}</td>
                  <td className="px-4 py-2.5 text-forest">{p.method || "—"}</td>
                  <td className="px-4 py-2.5 text-sage">{p.note || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-forest">
                    {fmtGBP(p.amount)}
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    <button
                      type="button"
                      onClick={() => onRemove(p.id)}
                      data-testid={`invoice-payment-remove-${i + 1}`}
                      className="p-1.5 text-forest/50 hover:text-red-600 transition-colors"
                      title="Remove payment"
                    >
                      <Trash2 size={14} strokeWidth={1.6} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {balance > 0 ? (
        <div
          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end"
          data-testid="invoice-payment-form"
        >
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-forest/60 mb-1.5">
              Amount
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={amount}
              data-testid="invoice-payment-amount"
              onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-white border border-forest/15 px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-forest/60 mb-1.5">
              Date
            </label>
            <input
              type="date"
              required
              value={date}
              data-testid="invoice-payment-date"
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-white border border-forest/15 px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-forest/60 mb-1.5">
              Method
            </label>
            <select
              value={method}
              data-testid="invoice-payment-method"
              onChange={(e) => setMethod(e.target.value)}
              className="w-full bg-white border border-forest/15 px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-forest/60 mb-1.5">
              Note (optional)
            </label>
            <input
              type="text"
              placeholder="e.g. 'Deposit'"
              value={note}
              data-testid="invoice-payment-note"
              onChange={(e) => setNote(e.target.value)}
              className="w-full bg-white border border-forest/15 px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
            />
          </div>
          <button
            type="button"
            disabled={submitting}
            data-testid="invoice-payment-record"
            onClick={submit}
            className="btn-forest !py-3 whitespace-nowrap disabled:opacity-60"
          >
            {submitting ? "Saving..." : "Record"}
          </button>
        </div>
      ) : (
        <p className="text-[12.5px] text-forest/60 bg-white border border-forest/10 px-4 py-3 text-center">
          This invoice is fully paid.
        </p>
      )}
    </section>
  );
};


export const InvoicesSection = ({ focus }) => {
  const [list, setList] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | invoice
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [success, setSuccess] = useState("");

  const refresh = async () => {
    try {
      setLoadError("");
      const [invs, cxs, s] = await Promise.all([
        axios.get(`${API}/admin/invoices`, authHeaders()),
        axios.get(`${API}/admin/customers`, authHeaders()),
        axios.get(`${API}/admin/invoice-settings`, authHeaders()),
      ]);
      setList(invs.data || []);
      setCustomers(cxs.data || []);
      setSettings(s.data.invoice_settings);
      setLogoUrl(s.data.logo_url);
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "The Invoices list didn't load.");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // When navigated from the Dashboard with a specific invoice id, auto-open
  // its editor once the list has loaded.
  useEffect(() => {
    if (!focus?.focusId || !list) return;
    const inv = list.find((i) => i.id === focus.focusId);
    if (inv) {
      setForm({ ...inv });
      setEditing(inv);
      setError("");
      setSuccess("");
    }
  }, [focus, list]);

  const openNew = () => {
    setForm(emptyInvoice(settings));
    setEditing("new");
    setError("");
    setSuccess("");
  };

  const openEdit = (inv) => {
    setForm({ ...inv });
    setEditing(inv);
    setError("");
    setSuccess("");
  };

  const close = () => {
    setEditing(null);
    setForm(null);
  };

  const pickCustomer = (customerId) => {
    if (!customerId) {
      setForm((f) => ({
        ...f,
        customer_id: "",
        customer_name: "",
        customer_company: "",
        customer_email: "",
        customer_address: "",
      }));
      return;
    }
    const c = customers.find((x) => x.id === customerId);
    if (!c) return;
    setForm((f) => ({
      ...f,
      customer_id: c.id,
      customer_name: c.name || "",
      customer_company: c.company || "",
      customer_email: c.email || "",
      customer_address: c.address || "",
    }));
  };

  // ---- Totals computed live from the editor's lines
  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, vat: 0, total: 0 };
    const sub = form.lines.reduce(
      (acc, l) => acc + Number(l.quantity || 0) * Number(l.unit_price || 0),
      0
    );
    const vat = form.vat_enabled ? sub * 0.2 : 0;
    return { subtotal: sub, vat, total: sub + vat };
  }, [form]);

  const vatAvailable = !!(settings?.vat_number || "").trim();

  const updateLine = (i, patch) => {
    setForm((f) => {
      const lines = f.lines.slice();
      lines[i] = { ...lines[i], ...patch };
      return { ...f, lines };
    });
  };

  const addLine = () =>
    setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.length > 1 ? f.lines.filter((_, idx) => idx !== i) : f.lines,
    }));

  const save = async (e, statusOverride) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const status = statusOverride || form.status || "sent";
      const payload = {
        customer_id: form.customer_id || null,
        customer_name: form.customer_name.trim(),
        customer_company: form.customer_company || "",
        customer_email: form.customer_email || "",
        customer_address: form.customer_address || "",
        invoice_date: form.invoice_date,
        due_date: form.due_date,
        lines: form.lines.map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
        })),
        vat_enabled: !!form.vat_enabled && vatAvailable,
        notes: form.notes || "",
        payment_terms: form.payment_terms || "",
        status,
      };
      if (editing === "new") {
        const { data } = await axios.post(`${API}/admin/invoices`, payload, authHeaders());
        setSuccess(status === "draft" ? "Draft saved." : "Invoice issued.");
        await refresh();
        // For brand-new invoices, open the just-created invoice so the user
        // stays on the same record (and can start recording payments).
        if (data && data.id) {
          const fresh = await axios.get(`${API}/admin/invoices/${data.id}`, authHeaders());
          setEditing(fresh.data);
          setForm({ ...fresh.data });
        }
      } else {
        await axios.put(`${API}/admin/invoices/${editing.id}`, payload, authHeaders());
        setSuccess("Saved.");
        await refresh();
        // Keep the user on the same invoice — do NOT close back to the list.
        const fresh = await axios.get(`${API}/admin/invoices/${editing.id}`, authHeaders());
        setEditing(fresh.data);
        setForm({ ...fresh.data });
      }
      // Auto-clear the success flash after a couple of seconds.
      setTimeout(() => setSuccess((s) => (s === "Saved." || s === "Draft saved." || s === "Invoice issued." ? "" : s)), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const addPayment = async (inv, payment) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/invoices/${inv.id}/payments`,
        payment,
        authHeaders()
      );
      await refresh();
      setEditing(data);
      setForm({ ...data });
      setSuccess(`Payment of ${fmtGBP(payment.amount)} recorded.`);
      setTimeout(() => setSuccess((s) => (s.startsWith("Payment of ") ? "" : s)), 3000);
      toast.success(`Payment of ${fmtGBP(payment.amount)} recorded.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not record payment");
    }
  };

  const removePayment = async (inv, paymentId) => {
    if (!window.confirm("Remove this payment? The invoice balance will be restored.")) return;
    try {
      const { data } = await axios.delete(
        `${API}/admin/invoices/${inv.id}/payments/${paymentId}`,
        authHeaders()
      );
      await refresh();
      setEditing(data);
      setForm({ ...data });
      toast.success("Payment removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove payment");
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null); // invoice or null

  const askRemove = (inv) => setConfirmDelete(inv);

  const confirmedRemove = async () => {
    const inv = confirmDelete;
    if (!inv) return;
    try {
      await axios.delete(`${API}/admin/invoices/${inv.id}`, authHeaders());
      setConfirmDelete(null);
      await refresh();
      toast.success(`${inv.number} removed. The invoice number stays reserved.`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete");
      setConfirmDelete(null);
    }
  };

  const downloadPdf = (inv) => {
    const html = buildInvoiceHtml({ invoice: inv, settings, logoUrl });
    printInvoice(html);
  };

  const emailPdf = async (inv) => {
    if (!inv.customer_email) {
      toast.error("No email saved for this customer.");
      return;
    }
    const html = buildInvoiceHtml({ invoice: inv, settings, logoUrl });
    try {
      await axios.post(
        `${API}/admin/invoices/${inv.id}/email`,
        { pdf_html: html },
        authHeaders()
      );
      await refresh();
      toast.success(`${inv.number} emailed to ${inv.customer_email}.`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) toast.error("Email service isn't configured.");
      else if (status === 502) toast.error("Email couldn't be delivered — check the address.");
      else toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Email failed");
    }
  };

  const sorted = useMemo(
    () =>
      (list || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.created_at || b.invoice_date) -
            new Date(a.created_at || a.invoice_date)
        ),
    [list]
  );

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((inv) => {
      if (statusFilter !== "all" && derivedStatus(inv) !== statusFilter) return false;
      if (!q) return true;
      const hay = `${inv.number || ""} ${inv.customer_name || ""} ${inv.customer_company || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [sorted, query, statusFilter]);

  if (loadError) {
    return (
      <SectionShell
        title="Invoices"
        subtitle="Create, send, download and track invoices. Numbers are sequential and format INV-YYYY-NNNNN."
        testid="admin-invoices-section"
      >
        <LoadError testid="invoices-load-error" onRetry={refresh} message={loadError} />
      </SectionShell>
    );
  }

  if (list === null || settings === null) return <p className="text-sm text-sage">Loading…</p>;

  return (
    <SectionShell
      title="Invoices"
      subtitle="Create, send, download and track invoices. Numbers are sequential and format INV-YYYY-NNNNN."
      testid="admin-invoices-section"
      action={
        !editing && (
          <button
            type="button"
            data-testid="invoices-new-button"
            onClick={openNew}
            className="btn-gold"
          >
            <Plus size={14} strokeWidth={2.2} /> New Invoice
          </button>
        )
      }
    >
      <Notice error={error} success={success} />

      {!vatAvailable && !editing && (
        <p data-testid="invoices-vat-hint" className="text-[12.5px] text-sage bg-cream-3 border border-forest/10 px-4 py-3 mb-6">
          Add a VAT number in <strong>Settings</strong> to enable the VAT switch on new invoices. Until then, all invoices are issued VAT-free.
        </p>
      )}

      {!editing && (
        <>
          {list.length === 0 && (
            <p className="text-sm text-sage bg-white border border-forest/10 px-6 py-10 text-center">
              No invoices yet — create your first.
            </p>
          )}
          {list.length > 0 && (
            <>
            {/* Search + status filter bar */}
            <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="invoices-filter-bar">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="invoices-search-input"
                  placeholder="Search by number, customer or company…"
                  className="w-full bg-white border border-forest/15 rounded pl-9 pr-3 py-2 text-[13px] focus:border-gold focus:outline-none"
                />
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.18em] uppercase text-forest/60"><Filter size={12} strokeWidth={1.8} /> Status</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="invoices-status-filter"
                className="bg-white border border-forest/15 rounded px-3 py-1.5 text-[12px] focus:border-gold focus:outline-none"
              >
                <option value="all">{`All (${sorted.length})`}</option>
                <option value="draft">Draft</option>
                <option value="unpaid">Unpaid</option>
                <option value="part_paid">Part Paid</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
              {filtered.length !== sorted.length && (
                <span className="text-[11px] text-sage">Showing {filtered.length} of {sorted.length}</span>
              )}
            </div>
            {filtered.length === 0 ? (
              <p className="text-sm text-sage bg-white border border-forest/10 px-6 py-10 text-center" data-testid="invoices-empty-filtered">
                No invoices match your search.
              </p>
            ) : (
            <>
            <div className="hidden lg:block bg-white border border-forest/10">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-forest/10 text-[10.5px] tracking-[0.18em] uppercase text-forest/60">
                    <th className="text-left px-5 py-3 font-bold">Number</th>
                    <th className="text-left px-4 py-3 font-bold">Customer</th>
                    <th className="text-left px-4 py-3 font-bold">Date</th>
                    <th className="text-left px-4 py-3 font-bold">Due</th>
                    <th className="text-right px-4 py-3 font-bold">Total</th>
                    <th className="text-right px-4 py-3 font-bold">Paid</th>
                    <th className="text-right px-4 py-3 font-bold">Balance</th>
                    <th className="text-left px-4 py-3 font-bold">Status</th>
                    <th className="w-56 px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv, i) => {
                    const s = derivedStatus(inv);
                    const paid = paidAmount(inv);
                    const balance = balanceDue(inv);
                    return (
                      <tr
                        key={inv.id}
                        data-testid={`invoice-row-${i + 1}`}
                        className="border-b border-forest/5 last:border-b-0 hover:bg-cream-3/50 transition-colors"
                      >
                        <td className="px-5 py-4 font-mono text-[13px] font-semibold text-forest">
                          {inv.number}
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-semibold text-forest">{inv.customer_name}</p>
                          {inv.customer_company && (
                            <p className="text-[12px] text-sage mt-0.5">{inv.customer_company}</p>
                          )}
                        </td>
                        <td className="px-4 py-4 text-[13px] text-sage">
                          {fmtDDMMYYYY(inv.invoice_date)}
                        </td>
                        <td className="px-4 py-4 text-[13px] text-sage">
                          {fmtDDMMYYYY(inv.due_date)}
                        </td>
                        <td className="px-4 py-4 text-right font-serif text-[18px] text-forest tabular-nums">
                          {fmtGBPWhole(inv.total)}
                        </td>
                        <td className="px-4 py-4 text-right text-[13px] text-forest/80 tabular-nums">
                          {fmtGBPWhole(paid)}
                        </td>
                        <td className="px-4 py-4 text-right text-[13px] font-semibold tabular-nums" data-testid={`invoice-balance-${i + 1}`}>
                          <span className={balance > 0 ? "text-gold-dark" : "text-forest/50"}>{fmtGBPWhole(balance)}</span>
                        </td>
                        <td className="px-4 py-4">
                          <StatusChip inv={inv} />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end gap-1 flex-wrap">
                            <button
                              type="button"
                              onClick={() => downloadPdf(inv)}
                              data-testid={`invoice-pdf-${i + 1}`}
                              className="p-2.5 text-forest/70 hover:text-forest transition-colors"
                              title="Download PDF"
                            >
                              <Download size={16} strokeWidth={1.6} />
                            </button>
                            <button
                              type="button"
                              onClick={() => emailPdf(inv)}
                              disabled={!inv.customer_email}
                              data-testid={`invoice-email-${i + 1}`}
                              className="p-2.5 text-forest/70 hover:text-forest transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title={inv.customer_email ? "Email to customer" : "Customer has no email on file"}
                            >
                              <Mail size={16} strokeWidth={1.6} />
                            </button>
                            {inv.status !== "draft" && balance > 0 && (
                              <button
                                type="button"
                                onClick={() => openEdit(inv)}
                                data-testid={`invoice-record-payment-${i + 1}`}
                                className="p-2.5 text-forest hover:text-gold-dark transition-colors"
                                title="Record payment"
                              >
                                <Wallet size={16} strokeWidth={1.6} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => openEdit(inv)}
                              data-testid={`invoice-edit-${i + 1}`}
                              className="p-2.5 text-forest/70 hover:text-forest transition-colors"
                              title={inv.status === "draft" ? "Edit draft" : "Edit invoice"}
                            >
                              <Pencil size={16} strokeWidth={1.6} />
                            </button>
                            <button
                              type="button"
                              onClick={() => askRemove(inv)}
                              data-testid={`invoice-delete-${i + 1}`}
                              className="p-2.5 text-forest/60 hover:text-red-600 transition-colors"
                              title="Delete invoice"
                            >
                              <Trash2 size={16} strokeWidth={1.6} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile / tablet-portrait cards */}
            <div className="lg:hidden space-y-3">
              {filtered.map((inv, i) => {
                const s = derivedStatus(inv);
                const paid = paidAmount(inv);
                const balance = balanceDue(inv);
                return (
                  <div
                    key={inv.id}
                    data-testid={`invoice-card-${i + 1}`}
                    className="bg-white border border-forest/10 p-4"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-mono text-[13px] font-semibold text-forest">{inv.number}</p>
                        <p className="font-semibold text-forest mt-1 truncate">{inv.customer_name}</p>
                        {inv.customer_company && (
                          <p className="text-[12px] text-sage mt-0.5 truncate">{inv.customer_company}</p>
                        )}
                      </div>
                      <StatusChip inv={inv} />
                    </div>
                    <div className="flex items-end justify-between gap-3 mt-3 pt-3 border-t border-forest/8">
                      <div className="text-[12px] text-sage leading-relaxed">
                        <p>Issued {fmtDDMMYYYY(inv.invoice_date)}</p>
                        <p>Due {fmtDDMMYYYY(inv.due_date)}</p>
                      </div>
                      <div className="text-right tabular-nums">
                        <p className="font-serif text-xl text-forest">{fmtGBPWhole(inv.total)}</p>
                        {paid > 0 && (
                          <p className="text-[11px] text-sage mt-0.5">
                            {fmtGBPWhole(paid)} paid · <span className={balance > 0 ? "text-gold-dark font-semibold" : "text-forest/60"}>{fmtGBPWhole(balance)} due</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-forest/8 justify-end">
                      <button type="button" onClick={() => downloadPdf(inv)} className="p-2.5 text-forest/70 hover:text-forest transition-colors" title="Download PDF"><Download size={16} strokeWidth={1.6} /></button>
                      <button type="button" onClick={() => emailPdf(inv)} disabled={!inv.customer_email} className="p-2.5 text-forest/70 hover:text-forest transition-colors disabled:opacity-30" title={inv.customer_email ? "Email to customer" : "No email on file"}><Mail size={16} strokeWidth={1.6} /></button>
                      {balance > 0 && inv.status !== "draft" && (
                        <button type="button" onClick={() => openEdit(inv)} className="p-2.5 text-forest hover:text-gold-dark transition-colors" title="Record payment"><Wallet size={16} strokeWidth={1.6} /></button>
                      )}
                      <button type="button" onClick={() => openEdit(inv)} className="p-2.5 text-forest/70 hover:text-forest transition-colors" title="Edit invoice"><Pencil size={16} strokeWidth={1.6} /></button>
                      <button type="button" onClick={() => askRemove(inv)} className="p-2.5 text-forest/60 hover:text-red-600 transition-colors" title="Delete invoice"><Trash2 size={16} strokeWidth={1.6} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
            )}
            </>
          )}
        </>
      )}

      {editing && form && (
        <form
          onSubmit={save}
          className="bg-white border border-forest/10 p-6 sm:p-8 space-y-6 max-w-5xl"
          data-testid="invoice-form"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl text-forest">
              {editing === "new" ? "New Invoice" : `Invoice ${editing.number}`}
            </h3>
            <button
              type="button"
              onClick={close}
              className="p-2 text-forest/60 hover:text-forest"
              data-testid="invoice-close-form"
              aria-label="Close editor"
            >
              <X size={18} strokeWidth={1.8} />
            </button>
          </div>

          {editing !== "new" && editing.status !== "draft" && (
            <p className="bg-cream border border-forest/15 text-ink text-[12.5px] px-4 py-3">
              This invoice has been issued. You can still edit everything here; existing payments stay as they are and the balance recalculates against the new total.
            </p>
          )}

          {editing !== "new" && editing.status !== "draft" && (
            <PaymentsPanel
              invoice={editing}
              onAdd={(p) => addPayment(editing, p)}
              onRemove={(pid) => removePayment(editing, pid)}
            />
          )}

          {/* Customer */}
          <div className="space-y-5">
            <div>
              <label className="block text-[11px] tracking-[0.16em] uppercase font-bold text-forest/60 mb-2">
                Pick a Saved Customer
              </label>
              <select
                data-testid="invoice-field-customer"
                value={form.customer_id || ""}
                onChange={(e) => pickCustomer(e.target.value)}
                className="w-full bg-white border border-forest/15 px-4 py-3 text-sm text-ink focus:border-gold focus:outline-none transition-colors"
              >
                <option value="">— ad-hoc customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.company ? ` · ${c.company}` : ""}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-sage mt-2">
                Need to add someone? Save them under the Customers section first.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <AField
                label="Customer Name"
                testid="invoice-field-name"
                required
                value={form.customer_name}
                onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
              />
              <AField
                label="Company (optional)"
                testid="invoice-field-company"
                value={form.customer_company}
                onChange={(e) => setForm((f) => ({ ...f, customer_company: e.target.value }))}
              />
              <AField
                label="Customer Email"
                testid="invoice-field-cemail"
                type="email"
                value={form.customer_email}
                onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))}
                hint="Required to email this invoice from the invoice list."
              />
              <ATextArea
                label="Billing Address"
                testid="invoice-field-caddress"
                rows={3}
                value={form.customer_address}
                onChange={(e) => setForm((f) => ({ ...f, customer_address: e.target.value }))}
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <AField
              label="Invoice Date"
              testid="invoice-field-date"
              type="date"
              required
              value={form.invoice_date}
              onChange={(e) => setForm((f) => ({ ...f, invoice_date: e.target.value }))}
            />
            <AField
              label="Due Date"
              testid="invoice-field-due"
              type="date"
              required
              value={form.due_date}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
            />
          </div>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[11px] tracking-[0.18em] uppercase font-bold text-forest">Service Lines</h4>
              <button
                type="button"
                onClick={addLine}
                data-testid="invoice-add-line"
                className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-gold-dark hover:text-forest transition-colors"
              >
                <Plus size={13} strokeWidth={2} /> Add Line
              </button>
            </div>
            <div className="bg-cream-3/60 border border-forest/10">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-forest/10 text-[9.5px] tracking-[0.2em] uppercase text-forest/60">
                    <th className="text-left px-3 py-2 font-bold">Description</th>
                    <th className="text-right px-3 py-2 font-bold w-24">Qty</th>
                    <th className="text-right px-3 py-2 font-bold w-32">Unit (£)</th>
                    <th className="text-right px-3 py-2 font-bold w-28">Amount</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((l, i) => {
                    const amount = Number(l.quantity || 0) * Number(l.unit_price || 0);
                    return (
                      <tr key={i} className="border-b border-forest/5 last:border-b-0 align-top">
                        <td className="p-2">
                          <input
                            data-testid={`invoice-line-desc-${i + 1}`}
                            type="text"
                            required
                            value={l.description}
                            onChange={(e) => updateLine(i, { description: e.target.value })}
                            placeholder="e.g. HMO licence advice"
                            className="w-full bg-white border border-forest/15 px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            data-testid={`invoice-line-qty-${i + 1}`}
                            type="number"
                            min="0"
                            step="0.5"
                            required
                            value={l.quantity}
                            onChange={(e) => updateLine(i, { quantity: e.target.value })}
                            className="w-full bg-white border border-forest/15 px-3 py-2 text-sm text-ink text-right focus:border-gold focus:outline-none"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            data-testid={`invoice-line-price-${i + 1}`}
                            type="number"
                            min="0"
                            step="0.01"
                            required
                            value={l.unit_price}
                            onChange={(e) => updateLine(i, { unit_price: e.target.value })}
                            className="w-full bg-white border border-forest/15 px-3 py-2 text-sm text-ink text-right focus:border-gold focus:outline-none"
                          />
                        </td>
                        <td className="p-2 text-right pt-4 font-medium text-forest">
                          {fmtGBP(amount)}
                        </td>
                        <td className="p-2 text-center pt-3">
                          {form.lines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLine(i)}
                              data-testid={`invoice-line-remove-${i + 1}`}
                              className="text-forest/50 hover:text-red-600 transition-colors"
                              title="Remove line"
                            >
                              <Trash2 size={15} strokeWidth={1.6} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* VAT toggle + totals */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start">
            <div>
              {vatAvailable ? (
                <label className="inline-flex items-center gap-3 text-[13px] font-semibold text-forest">
                  <input
                    type="checkbox"
                    data-testid="invoice-vat-toggle"
                    checked={!!form.vat_enabled}
                    onChange={(e) => setForm((f) => ({ ...f, vat_enabled: e.target.checked }))}
                    className="h-5 w-5 accent-forest"
                  />
                  Apply VAT at 20% (VAT No: <span className="font-mono text-ink">{settings.vat_number}</span>)
                </label>
              ) : (
                <p className="text-[12px] text-sage">
                  Add a VAT number in Settings to enable VAT on invoices.
                </p>
              )}
            </div>
            <div className="bg-cream-3 border border-forest/10 px-6 py-4 min-w-[260px]">
              <div className="flex justify-between py-1.5 text-[12.5px] text-forest/70">
                <span className="uppercase tracking-[0.14em] font-bold">Subtotal</span>
                <span className="font-mono" data-testid="invoice-subtotal">{fmtGBP(totals.subtotal)}</span>
              </div>
              {form.vat_enabled && vatAvailable && (
                <div className="flex justify-between py-1.5 text-[12.5px] text-forest/70">
                  <span className="uppercase tracking-[0.14em] font-bold">VAT 20%</span>
                  <span className="font-mono" data-testid="invoice-vat">{fmtGBP(totals.vat)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 mt-1 border-t border-forest/15">
                <span className="uppercase tracking-[0.14em] text-[12px] font-bold text-forest">
                  Total
                </span>
                <span className="font-serif text-[22px] text-gold-dark" data-testid="invoice-total">
                  {fmtGBP(totals.total)}
                </span>
              </div>
            </div>
          </div>

          <ATextArea
            label="Payment Terms"
            testid="invoice-field-terms"
            rows={2}
            value={form.payment_terms}
            onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))}
          />
          <ATextArea
            label="Notes (optional)"
            testid="invoice-field-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />

          <div className="flex gap-3 flex-wrap">
            {editing === "new" ? (
              <>
                <button
                  type="button"
                  data-testid="invoice-issue-button"
                  disabled={saving}
                  onClick={(e) => save(e, "sent")}
                  className="btn-gold !py-3.5 disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save & Issue Invoice"}
                </button>
                <button
                  type="button"
                  data-testid="invoice-save-draft-button"
                  disabled={saving}
                  onClick={(e) => save(e, "draft")}
                  className="btn-ghost-dark"
                >
                  Save as Draft
                </button>
              </>
            ) : (
              <button
                type="submit"
                data-testid="invoice-save-button"
                disabled={saving}
                className="btn-gold !py-3.5 disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            )}
            <button
              type="button"
              onClick={close}
              className="btn-ghost-dark"
              data-testid="invoice-cancel-button"
            >
              {editing === "new" ? "Discard" : "Cancel"}
            </button>
          </div>
        </form>
      )}

      {confirmDelete && (
        <div
          data-testid="invoice-delete-modal"
          className="fixed inset-0 z-50 bg-forest/50 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-cream max-w-md w-full p-7 border border-forest/15 shadow-[0_30px_80px_rgba(0,0,0,0.3)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-2xl text-forest mb-2">
              Delete {confirmDelete.number}?
            </h3>
            {(confirmDelete.payments || []).length > 0 ? (
              <>
                <p className="text-[13.5px] text-ink/80 leading-relaxed mb-4">
                  This invoice has{" "}
                  <strong className="text-forest">
                    {(confirmDelete.payments || []).length} payment
                    {(confirmDelete.payments || []).length === 1 ? "" : "s"}
                  </strong>{" "}
                  recorded totalling{" "}
                  <strong className="text-forest">
                    {fmtGBP(paidAmount(confirmDelete))}
                  </strong>
                  . Removing the invoice will hide it from your list — the invoice
                  number stays reserved and the record is preserved in your
                  database for your audit trail.
                </p>
                <p className="bg-gold-soft/25 border border-gold/40 text-ink text-[12px] leading-relaxed px-3 py-2 mb-5">
                  Make sure the payments are reconciled elsewhere before you
                  remove this invoice.
                </p>
              </>
            ) : (
              <p className="text-[13.5px] text-ink/80 leading-relaxed mb-5">
                This will remove invoice{" "}
                <strong className="text-forest">{confirmDelete.number}</strong>{" "}
                for <strong>{confirmDelete.customer_name}</strong> from your
                active list. The invoice number stays reserved so your numbering
                remains sequential.
              </p>
            )}
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={confirmedRemove}
                data-testid="invoice-delete-confirm"
                className="bg-red-700 hover:bg-red-800 text-cream px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors"
              >
                Yes, remove it
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                data-testid="invoice-delete-cancel"
                className="btn-ghost-dark"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionShell>
  );
};
